import React from 'react';
import {ITraveler, Department, DEPARTMENTS} from '../data/interfaces';
import {Button, Card} from 'react-bootstrap';
import {ADJUSTED_DEADLINE_LABEL, ADJUSTED_DEADLINE_TOOLTIP, formatAdjustedDeadline} from '../utils/adjustedDeadline';
import {
  getAssemblyTravelerStatusKind,
  getAssemblyTravelerVisualState,
} from '../utils/assemblyTravelerStatus';
import {getTravelerVisualColors, TRAVELER_OVERDUE_VISUAL_STATE} from '../utils/travelerVisualColors';
import AssemblyTravelerStatusBadge from './AssemblyTravelerStatusBadge';
import {isProductionTravelerOverdue} from '../utils/productionTravelerStatus';

interface UnscheduledTravelersProps {
  department: Department;
  unscheduledTravelers: ITraveler[];
  onTravelerClick: (travelerId: string) => void;
  onHideTraveler?: (traveler: ITraveler) => void;
  emptyMessage?: string;
  searchQuery?: string;
}

function getSearchHighlightTerms(searchQuery: string | undefined): string[] {
  if (!searchQuery) {
    return [];
  }

  return Array.from(new Set(
    searchQuery
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter((term) => term.length > 0)
  ));
}

function getHighlightLength(token: string, searchTerms: string[]): number {
  const normalizedToken = token.toLowerCase();
  const matchingTerm = searchTerms
    .filter((term) => normalizedToken.startsWith(term) || normalizedToken.includes(term))
    .sort((left, right) => right.length - left.length)[0];

  if (!matchingTerm) {
    return 0;
  }

  return matchingTerm.length;
}

function renderHighlightedText(text: string, searchTerms: string[]): React.ReactNode {
  if (searchTerms.length === 0) {
    return text;
  }

  const textParts: React.ReactNode[] = [];
  const tokenPattern = /[a-z0-9]+/gi;
  let lastIndex = 0;
  let tokenMatch: RegExpExecArray | null;

  while ((tokenMatch = tokenPattern.exec(text)) !== null) {
    const token = tokenMatch[0];
    const tokenStartIndex = tokenMatch.index;
    const highlightLength = getHighlightLength(token, searchTerms);

    if (highlightLength === 0) {
      continue;
    }

    if (tokenStartIndex > lastIndex) {
      textParts.push(text.slice(lastIndex, tokenStartIndex));
    }

    textParts.push(
      <em key={`${tokenStartIndex}-${highlightLength}`}>
        {text.slice(tokenStartIndex, tokenStartIndex + highlightLength)}
      </em>
    );
    lastIndex = tokenStartIndex + highlightLength;
  }

  if (lastIndex === 0) {
    return text;
  }

  if (lastIndex < text.length) {
    textParts.push(text.slice(lastIndex));
  }

  return textParts;
}

function getMillisecondsUntilNextLocalDay(date: Date): number {
  const nextLocalDay = new Date(date);
  nextLocalDay.setHours(24, 0, 0, 0);

  return nextLocalDay.getTime() - date.getTime();
}

function startTravelerVisualStateDateRefresh(
  setCurrentComparisonDate: React.Dispatch<React.SetStateAction<Date>>
): () => void {
  // Refresh after local midnight so overdue card colors stay accurate while the sidebar remains open.
  setCurrentComparisonDate(new Date());

  let visualStateDateRefreshTimeoutId: number | undefined;

  const scheduleTravelerVisualStateDateRefresh = () => {
    visualStateDateRefreshTimeoutId = window.setTimeout(() => {
      setCurrentComparisonDate(new Date());
      scheduleTravelerVisualStateDateRefresh();
    }, getMillisecondsUntilNextLocalDay(new Date()));
  };

  scheduleTravelerVisualStateDateRefresh();

  return () => {
    if (visualStateDateRefreshTimeoutId !== undefined) {
      window.clearTimeout(visualStateDateRefreshTimeoutId);
    }
  };
}

/**
 * Component to display unscheduled travelers.
 */
const UnscheduledTravelers: React.FC<UnscheduledTravelersProps> = ({
  department,
  unscheduledTravelers,
  onTravelerClick,
  onHideTraveler,
  emptyMessage,
  searchQuery,
}) => {
  const searchHighlightTerms = getSearchHighlightTerms(searchQuery);
  const [currentComparisonDate, setCurrentComparisonDate] = React.useState(() => new Date());
  const shouldShowUnscheduledTravelerDetails = department === DEPARTMENTS.assembly || department === DEPARTMENTS.production;
  const shouldRefreshTravelerVisualStateDate =
    department === DEPARTMENTS.assembly || department === DEPARTMENTS.production;

  React.useEffect(() => {
    if (!shouldRefreshTravelerVisualStateDate) {
      return;
    }

    return startTravelerVisualStateDateRefresh(setCurrentComparisonDate);
  }, [shouldRefreshTravelerVisualStateDate]);

  const handleHideClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    traveler: ITraveler
  ) => {
    event.stopPropagation();
    const confirmed = window.confirm('Hide this traveler from the Assembly task list? This cannot be undone.');
    if (!confirmed) return;
    onHideTraveler?.(traveler);
  };

  return (
    <div>
      {unscheduledTravelers.length > 0 ? (
        unscheduledTravelers.map((traveler) => {
          const statusKind = department === DEPARTMENTS.assembly
            ? getAssemblyTravelerStatusKind(traveler.status)
            : null;
          const assemblyVisualState = department === DEPARTMENTS.assembly
            ? getAssemblyTravelerVisualState(traveler.status, traveler.readyToShipDeadline, currentComparisonDate)
            : null;
          const productionVisualState = department === DEPARTMENTS.production
            && isProductionTravelerOverdue(traveler.readyToShipDeadline, currentComparisonDate)
            ? TRAVELER_OVERDUE_VISUAL_STATE
            : null;
          const visualState = assemblyVisualState ?? productionVisualState;
          const hasAssemblyVisualStateCard = visualState !== null;
          const visualColors = visualState ? getTravelerVisualColors(visualState) : null;
          const wasPreviouslyScheduled = traveler.wasPreviouslyScheduled === true;
          const cardClassName = [
            'm-3 position-relative unscheduled-traveler-card',
            wasPreviouslyScheduled ? 'unscheduled-traveler-card-previously-scheduled' : null,
            hasAssemblyVisualStateCard ? 'assembly-traveler-visual-state-card' : null,
            hasAssemblyVisualStateCard ? `assembly-traveler-visual-state-card-${visualState}` : null,
          ].filter(Boolean).join(' ');
          const cardStyle = visualColors
            ? ({
              '--assembly-traveler-card-bg': visualColors.backgroundColor,
              '--assembly-traveler-card-text': visualColors.textColor,
            } as React.CSSProperties)
            : undefined;

          return (
            <Card
              key={traveler.id}
              className={cardClassName}
              style={cardStyle}
              onClick={() => onTravelerClick(traveler.id)}
            >
              <Card.Body>
                <div className="d-flex align-items-start gap-2">
                  <div className="unscheduled-traveler-title flex-grow-1">
                    {renderHighlightedText(traveler.displayName, searchHighlightTerms)}
                  </div>
                  {statusKind && (
                    <AssemblyTravelerStatusBadge
                      className="unscheduled-traveler-title-status-badge"
                      statusKind={statusKind}
                    />
                  )}
                  {department === DEPARTMENTS.assembly && onHideTraveler && (
                    <Button
                      type="button"
                      // Use a light button on colorized cards so the Hide action stays readable against the card background.
                      variant={hasAssemblyVisualStateCard ? 'light' : 'outline-danger'}
                      size="sm"
                      onClick={(event) => handleHideClick(event, traveler)}
                    >
                      Hide
                    </Button>
                  )}
                </div>
                {shouldShowUnscheduledTravelerDetails && (
                  <div className="mt-2">
                    <div>
                      <span>Status: {renderHighlightedText(traveler.status || 'N/A', searchHighlightTerms)}</span>
                    </div>
                    <div>Item Type: {renderHighlightedText(traveler.itemTypeString || 'N/A', searchHighlightTerms)}</div>
                    <div title={ADJUSTED_DEADLINE_TOOLTIP}>
                      {ADJUSTED_DEADLINE_LABEL}: {renderHighlightedText(formatAdjustedDeadline(traveler.readyToShipDeadline), searchHighlightTerms)}
                    </div>
                    {traveler.portalLink && (
                      <a
                        href={traveler.portalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        View Traveler
                      </a>
                    )}
                  </div>
                )}
                {wasPreviouslyScheduled && (
                  <span
                    className="unscheduled-traveler-previously-scheduled-badge position-absolute bottom-0 end-0 d-inline-flex align-items-center text-nowrap fw-semibold"
                    title="This traveler was scheduled previously but has not been marked complete yet."
                  >
                    Previously scheduled
                  </span>
                )}
              </Card.Body>
            </Card>
          );
        })
      ) : (
        <p className='m-3'>{emptyMessage ?? 'No unscheduled travelers.'}</p>
      )}
    </div>
  );
};

export default UnscheduledTravelers;
